import * as React from 'react'

import { classList } from 'react-classlist-helper'

import { FormTypeConfiguratorExitContext } from '~/client/src/desktop/views/ProjectSetUp/components/PermitsSetUp/components/FormTypeConfigurator/FormTypeConfiguratorExitContext'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import WorkflowType from '~/client/src/shared/models/WorkflowType'

import { renderRestrictionChip } from '../WorkflowFieldRestrictionChip'
import WorkflowFieldRestrictionChipArea from '../WorkflowFieldRestrictionChipArea'
import { WorkflowFieldRestrictionItem } from '../WorkflowFieldRestrictionContext'
import WorkflowFieldRestrictionPanel from '../WorkflowFieldRestrictionPanel'
import LinkedWorkflowRestrictionProvider from './LinkedWorkflowRestrictionProvider'
import LinkedWorkflowSelectionContent from './LinkedWorkflowSelectionContent'

interface IProps {
  selectedTypes: WorkflowType[]
  allTypes: WorkflowType[]
  onSelectionChange: (selectedTypes: string[]) => void
}

const LinkedWorkflowRestriction: React.FC<IProps> = ({
  selectedTypes,
  allTypes,
  onSelectionChange,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const pendingSelectionRef = React.useRef<string[] | null>(null)

  const selectedTypeStrings = selectedTypes.map(pt => pt.type)

  const handlePanelOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open && pendingSelectionRef.current !== null) {
        onSelectionChange(pendingSelectionRef.current)
        pendingSelectionRef.current = null
      }
      setIsOpen(open)
    },
    [onSelectionChange],
  )

  const handleBufferedChange = React.useCallback((types: string[]) => {
    pendingSelectionRef.current = types
  }, [])

  const handleClose = React.useCallback(
    () => handlePanelOpenChange(false),
    [handlePanelOpenChange],
  )

  // The "Workflow Configurator" tooltip link leaves the form-type editor. Flush
  // the buffered selection and close the panel first (so nothing the user just
  // toggled is silently dropped), then route through the configurator's guarded
  // exit, which shows the UnsavedChangesDialog before discarding.
  const exitConfigurator = React.useContext(FormTypeConfiguratorExitContext)
  const handleManageInConfigurator = React.useMemo(
    () =>
      exitConfigurator
        ? () => {
            handleClose()
            exitConfigurator()
          }
        : undefined,
    [exitConfigurator, handleClose],
  )

  return (
    <LinkedWorkflowRestrictionProvider
      selectedTypes={selectedTypes}
      allTypes={allTypes}
      onSelectionChange={onSelectionChange}
    >
      <WorkflowFieldRestrictionPanel
        trigger={
          <WorkflowFieldRestrictionChipArea
            renderItem={renderWorkflowChip}
            isOpen={isOpen}
          />
        }
        open={isOpen}
        onOpenChange={handlePanelOpenChange}
        side="right"
        align="start"
        sideOffset={10}
        className="tw:flex tw:w-[400px] tw:max-h-[800px] tw:min-h-[600px] tw:flex-col"
      >
        <LinkedWorkflowSelectionContent
          selectedTypes={selectedTypeStrings}
          onSelectionChange={handleBufferedChange}
          onClose={handleClose}
          onManageInConfigurator={handleManageInConfigurator}
        />
      </WorkflowFieldRestrictionPanel>
    </LinkedWorkflowRestrictionProvider>
  )
}

function renderWorkflowChip(item: WorkflowFieldRestrictionItem): JSX.Element {
  return renderRestrictionChip(
    item,
    <WorkflowTypeIcon
      iconName={item.iconName}
      // Same muting the selector row uses, so a chip and its row read alike.
      className={classList({
        'tw:flex-none tw:ml-0.5 tw:mr-[5px]': true,
        'tw:opacity-40': !!item.isInactive,
      })}
      size="small"
    />,
  )
}

export default LinkedWorkflowRestriction
